import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  selectActiveProgramActionSlotNumber,
  selectActiveRunningProgramDisplayName,
  selectActiveRunningProgramSlotNumber,
} from '../../redux/ProjectSlice';
import { RootState } from '../../redux/Store';
import { RobotActions } from '../../utils/constants';
import usePrevious from './usePrevious';

const { PROGRAM } = RobotActions;

export type ProgramNotificationType = 'deploying' | 'deployed' | 'running' | 'stopped' | null;

export interface ProgramStateNotification {
  message: string;
  open: boolean;
  onClose: () => void;
  onExited: () => void;
  /** When true, snackbar does not auto-hide (for deploying/running) */
  persistent: boolean;
  type: ProgramNotificationType;
}

function resolveNotificationProgramName(
  robotDisplayName: string | null,
  cachedRobotDisplayName: string,
  capturedName: string,
  fallbackName: string,
  useCapturedContext: boolean
): string {
  if (robotDisplayName) {
    return robotDisplayName;
  }

  if (cachedRobotDisplayName) {
    return cachedRobotDisplayName;
  }

  return useCapturedContext ? capturedName || fallbackName : fallbackName;
}

function resolveNotificationSlotNumber(
  robotSlotNumber: number | null,
  cachedRobotSlotNumber: number | null,
  capturedSlotNumber: number | null,
  useCapturedContext: boolean
): number | null {
  if (robotSlotNumber !== null) {
    return robotSlotNumber;
  }

  if (cachedRobotSlotNumber !== null) {
    return cachedRobotSlotNumber;
  }

  return useCapturedContext ? capturedSlotNumber : null;
}

function assertUnhandledNotificationTemplate(templateKey: never): never {
  throw new Error(`Unhandled program notification template: ${String(templateKey)}`);
}

function buildProgramNotificationMessage(
  t: ReturnType<typeof useTranslation>['t'],
  templateKey:
    | 'deployingNoSlot'
    | 'deployingWithSlot'
    | 'runningNoSlot'
    | 'runningFromDriveUnit'
    | 'runningWithSlot'
    | 'deployed'
    | 'deployedWithSlot'
    | 'stopped',
  programName: string,
  slotNumber: number | null
): string {
  switch (templateKey) {
    case 'deployingNoSlot':
      return t('programState.toast.deployingNoSlot', {
        programName,
        defaultValue: '"{{programName}}" is Deploying',
      });
    case 'deployingWithSlot':
      return t('programState.toast.deployingWithSlot', {
        programName,
        slotNumber: String(slotNumber),
        defaultValue: '"{{programName}}" is Deploying to Slot {{slotNumber}}',
      });
    case 'runningFromDriveUnit':
      return t('programState.toast.runningFromDriveUnit', {
        programName,
        defaultValue: '"{{programName}}" is running from the Drive Unit',
      });
    case 'runningNoSlot':
      return t('programState.toast.runningNoSlot', {
        programName,
        defaultValue: '"{{programName}}" is running',
      });
    case 'runningWithSlot':
      return t('programState.toast.runningWithSlot', {
        programName,
        slotNumber: String(slotNumber),
        defaultValue: '"{{programName}}" is running from Slot {{slotNumber}}',
      });
    case 'deployed':
      return t('programState.toast.deployed', {
        programName,
        defaultValue: '"{{programName}}" has finished deploying!',
      });
    case 'deployedWithSlot':
      return t('programState.toast.deployedWithSlot', {
        programName,
        slotNumber: String(slotNumber),
        defaultValue: '"{{programName}}" has finished deploying to Slot {{slotNumber}}!',
      });
    case 'stopped':
      return t('programState.toast.stopped', {
        programName,
        defaultValue: '"{{programName}}" has stopped running',
      });
  }

  return assertUnhandledNotificationTemplate(templateKey);
}

/**
 * Hook to manage program deploy/run/stop notifications.
 * Shows snackbar messages for:
 * - Deploying: persistent until deploy completes or fails
 * - Deployed: brief toast when deploy-only completes
 * - Running: persistent until program stops
 * - Stopped: brief toast when program stops
 */
export const useProgramStateNotification = (): ProgramStateNotification => {
  const { t } = useTranslation();
  const { programState, lastRunMode } = useSelector((state: RootState) => state.project);
  const { loadedProject } = useSelector((state: RootState) => state.project);
  const { robotState } = useSelector((state: RootState) => state.robot);
  const activeProgramActionSlotNumber = useSelector(selectActiveProgramActionSlotNumber);
  const activeRunningProgramDisplayName = useSelector(selectActiveRunningProgramDisplayName);
  const activeRunningProgramSlotNumber = useSelector(selectActiveRunningProgramSlotNumber);

  const prevProgramState = usePrevious(programState);
  const programNameRef = useRef<string>('');
  const slotNumberRef = useRef<number | null>(null);
  const runningRobotProgramNameRef = useRef<string>('');
  const runningRobotSlotNumberRef = useRef<number | null>(null);
  // Program/slot copy is only concrete when captured from the deploy/run action, not from later selection changes.
  const actionContextCapturedRef = useRef(false);
  const typeRef = useRef<ProgramNotificationType>(null);
  const closingTypeRef = useRef<ProgramNotificationType>(null);
  const deployedToastShownRef = useRef(false);

  const [message, setMessage] = useState('');
  const [open, setOpen] = useState(false);
  const [persistent, setPersistent] = useState(false);
  const [type, setType] = useState<ProgramNotificationType>(null);

  const defaultProgramName = t('programState.toast.untitled', { defaultValue: 'Program' });
  const programName = loadedProject?.name || defaultProgramName;

  const showNotificationType = useCallback((nextType: Exclude<ProgramNotificationType, null>) => {
    closingTypeRef.current = null;
    typeRef.current = nextType;
    setType(nextType);
  }, []);

  const beginClosingNotification = useCallback(() => {
    setOpen(false);
    closingTypeRef.current = typeRef.current;
  }, []);

  const clearNotificationType = useCallback(() => {
    closingTypeRef.current = null;
    typeRef.current = null;
    setType(null);
  }, []);

  const getResolvedNotificationContext = useCallback(
    ({ fallbackName = programName, useCachedRobotContext = false }: { fallbackName?: string; useCachedRobotContext?: boolean } = {}) => {
      const useCapturedContext = actionContextCapturedRef.current;
      const cachedRobotName = useCachedRobotContext ? runningRobotProgramNameRef.current : '';
      const cachedRobotSlotNumber = useCachedRobotContext ? runningRobotSlotNumberRef.current : null;
      const name = resolveNotificationProgramName(
        activeRunningProgramDisplayName,
        cachedRobotName,
        programNameRef.current,
        fallbackName,
        useCapturedContext
      );
      const slotNumber = resolveNotificationSlotNumber(
        activeRunningProgramSlotNumber,
        cachedRobotSlotNumber,
        slotNumberRef.current,
        useCapturedContext
      );

      return { name, slotNumber };
    },
    [activeRunningProgramDisplayName, activeRunningProgramSlotNumber, programName]
  );

  useEffect(() => {
    if (activeRunningProgramDisplayName) {
      runningRobotProgramNameRef.current = activeRunningProgramDisplayName;
      runningRobotSlotNumberRef.current = activeRunningProgramSlotNumber;
      return;
    }

    const isNewLocalProgramAction =
      programState === PROGRAM.DOWNLOAD ||
      programState === PROGRAM.DOWNLOAD_RUN ||
      programState === PROGRAM.SEND_STARTING ||
      programState === PROGRAM.RUN ||
      programState === PROGRAM.STARTING;

    if (isNewLocalProgramAction) {
      runningRobotProgramNameRef.current = '';
      runningRobotSlotNumberRef.current = null;
    }
  }, [activeRunningProgramDisplayName, activeRunningProgramSlotNumber, programState]);

  useEffect(() => {
    const shouldCaptureActionSlot = programState === PROGRAM.SEND_STARTING || programState === PROGRAM.RUN || programState === PROGRAM.STARTING;

    if (shouldCaptureActionSlot && !actionContextCapturedRef.current) {
      programNameRef.current = programName;
      slotNumberRef.current = activeProgramActionSlotNumber;
      actionContextCapturedRef.current = true;
    }
  }, [programState, programName, activeProgramActionSlotNumber]);

  useEffect(() => {
    if (programState === PROGRAM.SEND_STARTING) {
      deployedToastShownRef.current = false;
      const { name, slotNumber } = getResolvedNotificationContext();
      setMessage(
        slotNumber === null
          ? buildProgramNotificationMessage(t, 'deployingNoSlot', name, slotNumber)
          : buildProgramNotificationMessage(t, 'deployingWithSlot', name, slotNumber)
      );
      setOpen(true);
      setPersistent(true);
      showNotificationType('deploying');
      return;
    }

    if (programState === PROGRAM.SEND_FAILED) {
      beginClosingNotification();
      slotNumberRef.current = null;
      runningRobotProgramNameRef.current = '';
      runningRobotSlotNumberRef.current = null;
      actionContextCapturedRef.current = false;
      return;
    }

    // If program is queued/starting/started, show running (this takes priority over deploy states)
    if (programState === PROGRAM.RUN || programState === PROGRAM.STARTING || programState === PROGRAM.STARTED) {
      const fallbackName = programState === PROGRAM.STARTED ? defaultProgramName : programName;
      const { name, slotNumber } = getResolvedNotificationContext({ fallbackName });
      const runningNoSlotTemplate = activeRunningProgramDisplayName === null ? 'runningNoSlot' : 'runningFromDriveUnit';
      setMessage(
        slotNumber === null
          ? buildProgramNotificationMessage(t, runningNoSlotTemplate, name, slotNumber)
          : buildProgramNotificationMessage(t, 'runningWithSlot', name, slotNumber)
      );
      setOpen(true);
      setPersistent(true);
      showNotificationType('running');
      return;
    }

    // Show "Deployed" only if deploy-only (not download_run) and not transitioning to running
    if (programState === PROGRAM.SEND_SUCCEEDED && lastRunMode === 'download') {
      if (deployedToastShownRef.current) {
        return;
      }

      deployedToastShownRef.current = true;
      const { name, slotNumber } = getResolvedNotificationContext();
      setMessage(
        slotNumber === null
          ? buildProgramNotificationMessage(t, 'deployed', name, slotNumber)
          : buildProgramNotificationMessage(t, 'deployedWithSlot', name, slotNumber)
      );
      setOpen(true);
      setPersistent(false);
      showNotificationType('deployed');
      slotNumberRef.current = null;
      actionContextCapturedRef.current = false;
      return;
    }

    // Close deploying snackbar when deploy succeeds for download_run (will show running next)
    if (programState === PROGRAM.SEND_SUCCEEDED && lastRunMode === 'download_run') {
      beginClosingNotification();
      return;
    }

    if ((programState === PROGRAM.STOPPED || programState === PROGRAM.STOP) && robotState !== RobotActions.STATE.CONNECTED) {
      beginClosingNotification();
      slotNumberRef.current = null;
      runningRobotProgramNameRef.current = '';
      runningRobotSlotNumberRef.current = null;
      actionContextCapturedRef.current = false;
      return;
    }

    if (
      (programState === PROGRAM.STOPPED || programState === PROGRAM.STOP) &&
      robotState === RobotActions.STATE.CONNECTED &&
      (prevProgramState === PROGRAM.STARTED ||
        prevProgramState === PROGRAM.STARTING ||
        prevProgramState === PROGRAM.RUN ||
        typeRef.current === 'running')
    ) {
      const { name } = getResolvedNotificationContext({ useCachedRobotContext: true });
      slotNumberRef.current = null;
      runningRobotProgramNameRef.current = '';
      runningRobotSlotNumberRef.current = null;
      actionContextCapturedRef.current = false;
      setMessage(buildProgramNotificationMessage(t, 'stopped', name, null));
      setOpen(true);
      setPersistent(false);
      showNotificationType('stopped');
      return;
    }

    if (programState === PROGRAM.STOPPED || programState === PROGRAM.STOP) {
      slotNumberRef.current = null;
      runningRobotProgramNameRef.current = '';
      runningRobotSlotNumberRef.current = null;
      actionContextCapturedRef.current = false;
    }
  }, [
    programState,
    prevProgramState,
    lastRunMode,
    defaultProgramName,
    programName,
    robotState,
    activeRunningProgramDisplayName,
    activeRunningProgramSlotNumber,
    beginClosingNotification,
    showNotificationType,
    getResolvedNotificationContext,
    t,
  ]);

  return {
    message,
    open,
    onClose: () => {
      if (typeRef.current === 'deploying' || typeRef.current === 'running') {
        return;
      }

      beginClosingNotification();
    },
    onExited: () => {
      const closingType = closingTypeRef.current;

      if (closingType === null || typeRef.current !== closingType) {
        return;
      }

      clearNotificationType();
    },
    persistent,
    type,
  };
};
